<script>
import { defineAsyncComponent, ref } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import AccountSelector from 'dashboard/components/layout/sidebarComponents/AccountSelector.vue';
import AddLabelModal from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import NotificationPanel from 'dashboard/routes/dashboard/notifications/components/NotificationPanel.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

import { emitter } from 'shared/helpers/mitt';
import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';
import ClosedSidebar from 'next/sidebar/ClosedSidebar.vue';
import AppFooter from '../../../shared/components/AppFooter.vue';
import VoiceCallModal from '../../components/VoiceCallModal.vue';
import { useAlert } from 'dashboard/composables';

export default {
  components: {
    NextSidebar,
    ClosedSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    AccountSelector,
    AddLabelModal,
    NotificationPanel,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    AppFooter,
    VoiceCallModal,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showAddLabelModal: false,
      showShortcutModal: false,
      isNotificationPanel: false,
      displayLayoutType: '',
      hasBanner: '',
      openNavbar: true,
      previousCall: null,
    };
  },
  computed: {
    ...mapGetters({
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentUser: 'getCurrentUser',
      showModal: 'voiceCall/showVoiceCallModal',
      currentCall: 'voiceCall/getCurrentCall',
      currentUserAvailability: 'getCurrentUserAvailability',
    }),
    currentRoute() {
      return ' ';
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'agent_list',
      ].includes(this.$route.name);
    },
    isSidebarOpen() {
      const { show_secondary_sidebar: showSecondarySidebar } = this.uiSettings;
      return showSecondarySidebar;
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
    previouslyUsedSidebarView() {
      const { previously_used_sidebar_view: showSecondarySidebar } =
        this.uiSettings;
      return showSecondarySidebar;
    },
    showNextSidebar() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.CHATWOOT_V4
      );
    },
  },
  watch: {
    displayLayoutType() {
      const { LAYOUT_TYPES } = wootConstants;
      this.updateUISettings({
        conversation_display_type:
          this.displayLayoutType === LAYOUT_TYPES.EXPANDED
            ? LAYOUT_TYPES.EXPANDED
            : this.previouslyUsedDisplayType,
        show_secondary_sidebar:
          this.displayLayoutType === LAYOUT_TYPES.EXPANDED
            ? false
            : this.previouslyUsedSidebarView,
      });
    },
    currentCall(newCall, oldCall) {
      if (oldCall !== null && newCall === null) {
        this.showCallEndedAlert();
      }
      this.previousCall = oldCall;
    },
    currentUserAvailability(value) {
      this.setUserAvailability(value);
    },
  },
  mounted() {
    this.initializeClassicSipConnection();
    this.previousCall = this.currentCall;
    this.handleResize();
    this.$nextTick(this.checkBanner);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('resize', this.checkBanner);
    emitter.on(BUS_EVENTS.TOGGLE_SIDEMENU, this.toggleSidebar);
  },
  unmounted() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('resize', this.checkBanner);
    emitter.off(BUS_EVENTS.TOGGLE_SIDEMENU, this.toggleSidebar);
  },

  methods: {
    ...mapActions({
      initializeVoice: 'voiceCall/initialize',
      setUserAvailability: 'voiceCall/setUserAvailability',
    }),
    showCallEndedAlert() {
      useAlert(this.$t('VOICE_CALL.API.CALL_ENDED'));
      this.previousCall = null;
    },
    async initializeClassicSipConnection() {
      const user = this.currentUser;
      const userAvailability = this.currentUserAvailability;
      const sipConfig = {
        sip_extension: user.sip_extension,
        sip_password: user.sip_password,
        server: import.meta.env.VITE_SYNVOX_PBX_URL,
        app_url: import.meta.env.VITE_SYNVOX_APP_URL,
        display_name: user.name,
        userAvailability: userAvailability,
      };
      try {
        await this.initializeVoice(sipConfig);
      } catch (error) {
        useAlert(this.$t('VOICE_CALL.API.ERRORS.INITIALIZING'));
      }
    },
    checkBanner() {
      this.hasBanner =
        document.getElementsByClassName('woot-banner').length > 0;
    },
    handleResize() {
      const { SMALL_SCREEN_BREAKPOINT, LAYOUT_TYPES } = wootConstants;
      let throttled = false;
      const delay = 150;

      if (throttled) {
        return;
      }
      throttled = true;

      setTimeout(() => {
        throttled = false;
        if (window.innerWidth <= SMALL_SCREEN_BREAKPOINT) {
          this.displayLayoutType = LAYOUT_TYPES.EXPANDED;
          this.openNavbar = false;
        } else {
          this.displayLayoutType = LAYOUT_TYPES.CONDENSED;
          this.openNavbar = true;
        }
      }, delay);
    },
    toggleSidebar() {
      this.updateUISettings({
        show_secondary_sidebar: !this.isSidebarOpen,
        previously_used_sidebar_view: !this.isSidebarOpen,
      });
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    showAddLabelPopup() {
      this.showAddLabelModal = true;
    },
    hideAddLabelPopup() {
      this.showAddLabelModal = false;
    },
    openNotificationPanel() {
      this.isNotificationPanel = true;
    },
    closeNotificationPanel() {
      this.isNotificationPanel = false;
    },
    changeNavbar() {
      this.openNavbar = !this.openNavbar;
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-screen">
    <main class="flex-1 flex dark:text-slate-300 pt-4 pb-2 pl-4 overflow-auto">
      <NextSidebar
        v-if="openNavbar"
        @toggle-account-modal="toggleAccountModal"
        @open-key-shortcut-modal="toggleKeyShortcutModal"
        @close-key-shortcut-modal="closeKeyShortcutModal"
        @show-create-account-modal="openCreateAccountModal"
        @change-navbar="changeNavbar"
      />
      <ClosedSidebar
        v-else
        @toggle-account-modal="toggleAccountModal"
        @open-key-shortcut-modal="toggleKeyShortcutModal"
        @close-key-shortcut-modal="closeKeyShortcutModal"
        @show-create-account-modal="openCreateAccountModal"
        @change-navbar="changeNavbar"
      />
      <div class="flex-1 flex px-0 min-h-0">
        <UpgradePage
          v-show="showUpgradePage"
          ref="upgradePageRef"
          :bypass-upgrade-page="bypassUpgradePage"
        />
        <template v-if="!showUpgradePage">
          <router-view />
          <CommandBar />
          <CopilotLauncher />
          <CopilotContainer />
          <NotificationPanel
            v-if="isNotificationPanel"
            @close="closeNotificationPanel"
          />
          <woot-modal
            v-model:show="showAddLabelModal"
            :on-close="hideAddLabelPopup"
          >
            <AddLabelModal @close="hideAddLabelPopup" />
          </woot-modal>
        </template>
        <AccountSelector
          :show-account-modal="showAccountModal"
          @close-account-modal="toggleAccountModal"
          @show-create-account-modal="openCreateAccountModal"
        />
        <AddAccountModal
          :show="showCreateAccountModal"
          @close-account-create-modal="closeCreateAccountModal"
        />
        <WootKeyShortcutModal
          v-model:show="showShortcutModal"
          @close="closeKeyShortcutModal"
          @clickaway="closeKeyShortcutModal"
        />
      </div>
      <VoiceCallModal v-if="showModal" />
    </main>
    <AppFooter />
  </div>
</template>
